import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { BriefBulletsDagService } from '@/services/briefBullets/briefBulletsDagService';
import { Loader2, CheckCircle, XCircle, Clock, RefreshCw, Play, Pause, Trash2, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { toast as sonnerToast } from 'sonner';
import { SupabaseStorageService } from '@/services/photo/supabaseStorageService';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export const BriefBulletsDagManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [ntbId, setNtbId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [photoUrl, setPhotoUrl] = useState('/images/kai-logo.png');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const { data: taskStatus, isLoading: taskLoading } = useQuery({
    queryKey: ['brief-bullets-dag-task'],
    queryFn: async () => {
      const task = await BriefBulletsDagService.getTaskStatus();
      // Load photo URL from task config
      if (task?.config?.photo_url) {
        setPhotoUrl(task.config.photo_url);
      }
      return task;
    },
    refetchInterval: 10000,
  });

  const { data: todayStatus, isLoading: todayLoading } = useQuery({
    queryKey: ['brief-bullets-dag-today'],
    queryFn: () => BriefBulletsDagService.getTodayStatus(),
    refetchInterval: 10000,
  });

  const { data: history = [], isLoading: historyLoading } = useQuery({
    queryKey: ['brief-bullets-dag-history'],
    queryFn: () => BriefBulletsDagService.getPublishHistory(7),
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['brief-bullets-dag-logs'],
    queryFn: () => BriefBulletsDagService.getTaskLogs(5),
  });

  const isLoading = taskLoading || todayLoading || historyLoading || logsLoading;

  const publishMutation = useMutation({
    mutationFn: ({ ntbId, searchDate }: { ntbId?: string; searchDate?: string }) =>
      BriefBulletsDagService.triggerManualPublish(ntbId, searchDate),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Publisert!',
          description: 'Brief Bullets er nå publisert',
        });
        setNtbId('');
        setSelectedDate('');
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-task'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-today'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-history'] });
      } else {
        toast({
          title: 'Feil ved publisering',
          description: result.error || 'Ukjent feil',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Feil',
        description: 'Kunne ikke publisere artikkel',
        variant: 'destructive',
      });
    },
  });

  const handleManualPublish = () => {
    if (ntbId.trim()) {
      if (!selectedDate) {
        sonnerToast.error('Velg en dato når du bruker NTB ID');
        return;
      }

      const raw = ntbId.trim();
      const isNumeric = /^\d+$/.test(raw);
      const isNtbId = /^NTB[A-Z0-9-]+$/i.test(raw);
      
      if (!isNumeric && !isNtbId) {
        sonnerToast.error('Ugyldig ID. Bruk tall (f.eks. 19371332) eller NTB-ID (f.eks. NTBxxxx-...).');
        return;
      }
    }

    publishMutation.mutate({
      ntbId: ntbId.trim() || undefined,
      searchDate: selectedDate || undefined,
    });
  };

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) => BriefBulletsDagService.toggleAutomaticPublishing(enable),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-task'] });
      await queryClient.refetchQueries({ queryKey: ['brief-bullets-dag-task'] });
      toast({
        title: 'Innstillinger oppdatert',
        description: 'Automatisk publisering er oppdatert.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Feil',
        description: error instanceof Error ? error.message : 'Kunne ikke oppdatere innstillinger',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => BriefBulletsDagService.deleteTodayArticle(),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Slettet!',
          description: 'Dagens Brief Bullets artikkel er slettet',
        });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-today'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-history'] });
      } else {
        toast({
          title: 'Feil ved sletting',
          description: result.error || 'Ukjent feil',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Feil',
        description: 'Kunne ikke slette artikkel',
        variant: 'destructive',
      });
    },
  });

  const handleDeleteToday = () => {
    if (!confirm('Er du sikker på at du vil slette dagens Brief Bullets artikkel?')) {
      return;
    }
    deleteMutation.mutate();
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    try {
      const uploadedUrl = await SupabaseStorageService.uploadImage(file, 'profile-images');
      setPhotoUrl(uploadedUrl);
      
      // Save photo URL - use rpc() to avoid type issues
      const { error } = await (supabase as any).rpc('update_briefr_bullets_dag_config', {
        new_photo_url: uploadedUrl
      });

      if (error) {
        console.error('Error saving photo URL:', error);
        // If RPC doesn't exist, just continue anyway
      }

      toast({
        title: 'Bilde lastet opp',
        description: 'Profilbildet er oppdatert',
      });
      
      queryClient.invalidateQueries({ queryKey: ['brief-bullets-dag-task'] });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: 'Feil',
        description: 'Kunne ikke laste opp bilde',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">Aktiv</Badge>;
      case 'paused':
        return <Badge variant="secondary">Pauset</Badge>;
      case 'failed':
        return <Badge variant="destructive">Feilet</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          ☀️ Briefr Bullets: Hva har skjedd i dag?
        </CardTitle>
        <CardDescription>
          Automatisk publisering av dagens NTB-oversikt kl. 15:30
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Status i dag</h3>
            {todayStatus?.published ? (
              <Badge variant="default" className="bg-green-500">
                <CheckCircle className="h-3 w-3 mr-1" />
                Publisert
              </Badge>
            ) : (
              <Badge variant="secondary">
                <Clock className="h-3 w-3 mr-1" />
                Venter
              </Badge>
            )}
          </div>

          {todayStatus?.article && (
            <div className="text-sm text-muted-foreground">
              Publisert kl. {format(new Date(todayStatus.article.created_at), 'HH:mm', { locale: nb })}
            </div>
          )}

          {taskStatus?.error_message && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Siste feil:</p>
                <p>{taskStatus.error_message}</p>
                {taskStatus.retry_count > 0 && (
                  <p className="mt-1 text-xs">
                    Forsøk {taskStatus.retry_count} av {taskStatus.config?.max_retries || 1}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Task Configuration */}
        {taskStatus && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Konfigurasjon</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">{getStatusBadge(taskStatus.status)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Publiseringstid:</span>
                <div className="mt-1 font-medium">{taskStatus.schedule_time?.slice(0, 5)}</div>
              </div>
              {taskStatus.config?.retry_time && (
                <div>
                  <span className="text-muted-foreground">Retry-tid:</span>
                  <div className="mt-1 font-medium">{taskStatus.config.retry_time.slice(0, 5)}</div>
                </div>
              )}
              {taskStatus.next_run && (
                <div>
                  <span className="text-muted-foreground">Neste kjøring:</span>
                  <div className="mt-1 font-medium">
                    {format(new Date(taskStatus.next_run), 'dd.MM HH:mm', { locale: nb })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Upload */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Profilbilde for Kai</h3>
          <div className="flex items-center gap-3">
            <img 
              src={photoUrl} 
              alt="Kai profil" 
              className="w-16 h-16 rounded-full object-cover border-2 border-border"
            />
            <div className="flex-1">
              <input
                type="file"
                id="photo-upload-dag"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                disabled={isUploadingPhoto}
                onClick={() => document.getElementById('photo-upload-dag')?.click()}
              >
                {isUploadingPhoto ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Laster opp...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Last opp nytt bilde
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Dette bildet brukes som profilbilde i Brief Bullets artiklene
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="space-y-3 mb-3">
            <h3 className="text-sm font-medium">Manuell publisering (valgfritt)</h3>
            <div className="flex gap-2">
              <Input
                placeholder="dd.mm.yyyy"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="flex-1"
              />
              <Input
                placeholder="NTB ID (f.eks. 19371332)"
                value={ntbId}
                onChange={(e) => setNtbId(e.target.value)}
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              La feltene stå tomme for å bruke dagens "Dagens viktigste nyheter så langt" artikkel, eller fyll inn for å velge en spesifikk NTB-artikkel
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleManualPublish}
              disabled={publishMutation.isPending}
              className="flex-1"
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publiserer...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Publiser nå
                </>
              )}
            </Button>
            <Button
              onClick={() => queryClient.invalidateQueries()}
              variant="outline"
              size="icon"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => toggleMutation.mutate(taskStatus?.status !== 'active')}
              disabled={toggleMutation.isPending}
              variant={taskStatus?.status === 'paused' ? 'default' : 'secondary'}
              className="flex-1"
            >
              {toggleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Endrer...
                </>
              ) : taskStatus?.status === 'paused' ? (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Aktiver automatisk publisering
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause automatisk publisering
                </>
              )}
            </Button>
            <Button
              onClick={handleDeleteToday}
              disabled={deleteMutation.isPending || !todayStatus?.published}
              variant="destructive"
              size="icon"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Siste publiseringer</h3>
            <div className="space-y-2">
              {history.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center justify-between p-2 rounded-lg border bg-card text-sm"
                >
                  <div>
                    <div className="font-medium">
                      {format(new Date(article.created_at), 'dd. MMMM yyyy', { locale: nb })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Kl. {format(new Date(article.created_at), 'HH:mm', { locale: nb })}
                    </div>
                  </div>
                  <Badge variant="default" className="bg-green-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Publisert
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Logs */}
        {logs.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Logg</h3>
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-2 p-2 rounded-lg border bg-card text-sm"
                >
                  {log.status === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : log.status === 'failed' ? (
                    <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{log.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: nb })}
                      {log.execution_time_ms && ` • ${log.execution_time_ms}ms`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
